import { Add, Image } from 'assets/icons/revamp';
import LoadingIndicator from 'components/common/LoadingIndicator';
import BaseButton from 'components/ui/BaseButton';
import COLOR from 'constants/color';
import { ATTRIB } from 'constants/test';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { t } from 'services/i18n.service';
import {
  uploadActivityImage,
  ActivityImageUploadResult
} from 'services/image-upload';
import {
  ACTIVITY_IMAGE_ACCEPT_ATTRIBUTE,
  UPLOAD_FILE_BUTTON_VARIANT,
  UploadFileButtonVariant
} from 'constants/image';

interface UploadFileButtonProps {
  setUrls: (result: ActivityImageUploadResult[]) => void;
  activityId: string;
  variant?: UploadFileButtonVariant;
}

export default function UploadFileButton({
  setUrls,
  activityId,
  variant = UPLOAD_FILE_BUTTON_VARIANT.BUTTON
}: UploadFileButtonProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadState, setUploadState] = useState<{
    isUploading: boolean;
    progress: number;
  }>({
    isUploading: false,
    progress: 0
  });

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (!file || uploadState.isUploading) return;

    setUploadState({ isUploading: true, progress: 0 });
    try {
      const result = await uploadActivityImage(activityId, file, {
        onProgress: (progressPercent) => {
          setUploadState((prev) => ({ ...prev, progress: progressPercent }));
        }
      });
      setUrls([result]);
    } catch (err: unknown) {
      const errorKey =
        err instanceof Error && err.message
          ? err.message
          : 'image_upload.upload_failed';
      toast.error(t(errorKey));
    } finally {
      setUploadState({ isUploading: false, progress: 0 });
    }
  };

  return (
    <>
      {variant === UPLOAD_FILE_BUTTON_VARIANT.TILE ? (
        <button
          type='button'
          data-testid={ATTRIB.DATA_TESTID.UPLOAD_FILE_BUTTON}
          aria-label={t('tabs_revamp.instructions_tab.upload_an_image')}
          title={t('tabs_revamp.instructions_tab.upload_an_image')}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadState.isUploading || !activityId}
          className='relative ~w-20/24 ~h-20/24 bg-gray-200 dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden group flex items-center justify-center transition-colors duration-200 hover:border-primary-500/60 dark:hover:border-primary-dark-500 disabled:cursor-not-allowed disabled:opacity-70'>
          {uploadState.isUploading ? (
            <div className='absolute inset-0 flex flex-col items-center justify-center gap-1 bg-gray-200/80 dark:bg-gray-700/80 z-10 pointer-events-none'>
              <LoadingIndicator dims='~w-6/8 ~h-6/8' />
              <span className='~text-[0.625rem]/xs font-medium text-gray-600 dark:text-gray-300'>
                {uploadState.progress}%
              </span>
            </div>
          ) : (
            <div className='relative flex items-center justify-center'>
              <Image
                styles='~w-10/12 ~h-10/12'
                fill={COLOR.GRAY[500]}
              />
              <span className='absolute -right-2 -bottom-1 flex items-center justify-center rounded-full bg-white/90 dark:bg-gray-800/90 border border-gray-300 dark:border-gray-600 p-0.5'>
                <Add
                  styles='~w-3/5 ~h-3/5'
                  fill={COLOR.PRIMARY.LIGHT_900}
                />
              </span>
            </div>
          )}
        </button>
      ) : (
        <BaseButton
          data-testid={ATTRIB.DATA_TESTID.UPLOAD_FILE_BUTTON}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadState.isUploading || !activityId}
          size='xs'
          variant='secondary'>
          {uploadState.isUploading
            ? `${t('uploading')} ${uploadState.progress}%`
            : t('add_image')}
        </BaseButton>
      )}
      <input
        ref={fileInputRef}
        type='file'
        accept={ACTIVITY_IMAGE_ACCEPT_ATTRIBUTE}
        onChange={handleFileChange}
        className='hidden'
      />
    </>
  );
}
